/**
 * Clerk auth adapter — real auth. The only frontend module that imports
 * `@clerk/clerk-react`. Mirrors the previous __root.tsx wiring exactly, so clerk
 * mode behaves as before.
 */
import { ClerkProvider, SignIn, SignedIn, SignedOut, UserButton } from '@clerk/clerk-react';
import type { ReactNode } from 'react';
import type { AuthAdapter } from './types';

/**
 * Read the publishable key lazily (inside the component, not at module load) so
 * the missing-key throw only fires when the clerk adapter is actually mounted —
 * never in dev mode, where this module is imported but unused.
 */
function ClerkAuthProvider({ children }: { children: ReactNode }) {
  const publishableKey = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;
  if (!publishableKey) {
    throw new Error('Missing VITE_CLERK_PUBLISHABLE_KEY (required when VITE_AUTH_MODE=clerk)');
  }
  return (
    <ClerkProvider publishableKey={publishableKey} afterSignOutUrl="/">
      {children}
    </ClerkProvider>
  );
}

function ClerkGate({ children }: { children: ReactNode }) {
  return (
    <>
      <SignedIn>{children}</SignedIn>
      <SignedOut>
        <div className="flex min-h-screen items-center justify-center p-4">
          <SignIn routing="hash" />
        </div>
      </SignedOut>
    </>
  );
}

export const clerkAdapter: AuthAdapter = {
  mode: 'clerk',
  Provider: ClerkAuthProvider,
  Gate: ClerkGate,
  UserMenu: () => <UserButton afterSignOutUrl="/" />,
  async decorateRequest(headers) {
    // Clerk attaches a global `window.Clerk` once <ClerkProvider> has loaded;
    // `getToken()` returns a short-lived session JWT.
    const clerk = (
      window as unknown as { Clerk?: { session?: { getToken(): Promise<string | null> } } }
    ).Clerk;
    const token = clerk?.session ? await clerk.session.getToken() : null;
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
  },
};
